import React, { useState, useEffect } from 'react';
import { 
    ref, 
    uploadBytes, 
    getDownloadURL 
} from "firebase/storage";
import { 
    EmailAuthProvider, 
    reauthenticateWithCredential, 
    updatePassword, 
} from "firebase/auth";
import { storage, appId } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getCalculatedAge } from '../utils/helpers';
import MessageBox from '../components/MessageBox';
import AddPetModal from '../components/AddPetModal';

function ProfilePage() {
    const { userDetails, updateUserProfile, currentUser, logout } = useAuth();
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [firstNameState, setFirstNameState] = useState('');
    const [lastNameState, setLastNameState] = useState('');
    const [displayNameState, setDisplayNameState] = useState(''); 
    
    const [newColor, setNewColor] = useState('');
    const [homeAddress, setHomeAddress] = useState('');
    const [emergencyContactName, setEmergencyContactName] = useState('');
    const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
    const [cellPhoneNumber, setCellPhoneNumber] = useState('');
    const [pets, setPets] = useState([]);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [loading, setLoading] = useState(false);
    const [isAddPetModalOpen, setIsAddPetModalOpen] = useState(false);
    const [editingPet, setEditingPet] = useState(null);
    
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [docToRemove, setDocToRemove] = useState(null);
    const [confirmDeactivate, setConfirmDeactivate] = useState(false); 

    useEffect(() => {
        if (userDetails) {
            setFirstNameState(userDetails.firstName || '');
            setLastNameState(userDetails.lastName || '');
            setDisplayNameState(userDetails.preferredDisplayName || userDetails.displayName || '');

            setNewColor(userDetails.color || '#60a5fa');
            setHomeAddress(userDetails.homeAddress || '');
            setEmergencyContactName(userDetails.emergencyContactName || '');
            setEmergencyContactPhone(userDetails.emergencyContactPhone || '');
            setCellPhoneNumber(userDetails.cellPhoneNumber || '');
            setPets(userDetails.pets ? userDetails.pets.map(p => ({ ...p, id: p.id || Date.now() + Math.random() })) : []);
        }
    }, [userDetails]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            const updates = {
                firstName: firstNameState, 
                lastName: lastNameState,
                preferredDisplayName: displayNameState, 
                color: newColor,
                homeAddress: homeAddress,
                emergencyContactName: emergencyContactName,
                emergencyContactPhone: emergencyContactPhone,
                cellPhoneNumber: cellPhoneNumber
            };
            
            if (profilePicFile) {
                if (!currentUser || !currentUser.uid) {
                    throw new Error("User ID is unavailable for file upload.");
                }
                const fileName = `${Date.now()}_${profilePicFile.name}`;
                const storageRef = ref(storage, `artifacts/${appId}/users/${currentUser.uid}/profilePictures/${fileName}`);
                await uploadBytes(storageRef, profilePicFile);
                updates.userProfilePictureUrl = await getDownloadURL(storageRef);
            }

            await updateUserProfile(updates);
            setMessage('Profile updated successfully!');
            setMessageType('info');
            setProfilePicFile(null);
        }
        catch (error) {
            console.error("Error updating profile:", error);
            if (error.code === 'storage/unauthorized') {
                    setMessage("Error updating profile: Storage permission denied. Please ensure you have deployed the correct Firebase Storage Security Rules allowing users to write to their own folder.");
            } else {
                    setMessage(`Error updating profile: ${error.message}`);
            }
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);

        if (!currentUser || !currentUser.email) {
                setMessage("Authentication error. Please log out and log back in.");
                setMessageType('error');
                setLoading(false);
                return;
        }
        
        if (!currentPassword) {
            setMessage("Please enter your current password to proceed.");
            setMessageType('error');
            setLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage("New password and confirmation password do not match.");
            setMessageType('error');
            setLoading(false);
            return;
        }
        if (newPassword.length < 6) {
            setMessage("New password must be at least 6 characters long.");
            setMessageType('error');
            setLoading(false);
            return;
        }
        
        try {
            const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            await updatePassword(currentUser, newPassword);
            
            setMessage("Password changed successfully.");
            setMessageType('info');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            let errorMessage = `Error changing password: ${error.message}`;
            if (error.code === 'auth/wrong-password') {
                errorMessage = "Error: The current password you entered is incorrect.";
            } else if (error.code === 'auth/user-mismatch') {
                errorMessage = "Error: User verification failed. Please try logging in again.";
            }
            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAddPetModal = () => {
        setEditingPet(null);
        setIsAddPetModalOpen(true);
        setMessage('');
    };

    const handleEditPetClick = (pet) => {
        setEditingPet(pet);
        setIsAddPetModalOpen(true);
        setMessage('');
    };

    const handleSavePet = async (petData) => {
        setMessage('');
        setLoading(true);
        try {
            let petToProcess = { ...petData };
            
            if (petData.pictureFile) {
                const file = petData.pictureFile;
                const petFileName = `${Date.now()}_${petData.name}_${file.name}`;
                const petStorageRef = ref(storage, `artifacts/${appId}/users/${currentUser.uid}/petPictures/${petFileName}`);
                await uploadBytes(petStorageRef, file);
                petToProcess.pictureUrl = await getDownloadURL(petStorageRef);
            }
            
            if (petData.newDocuments && petData.newDocuments.length > 0) {
                const uploadedDocumentUrls = [];
                for (const docFile of petData.newDocuments) {
                    const docFileName = `${Date.now()}_${petData.name}_${docFile.name}`;
                    const docStorageRef = ref(storage, `artifacts/${appId}/users/${currentUser.uid}/petDocuments/${docFileName}`);
                    await uploadBytes(docStorageRef, docFile);
                    const docDownloadURL = await getDownloadURL(docStorageRef);
                    uploadedDocumentUrls.push({ name: docFile.name, url: docDownloadURL });
                }
                petToProcess.documents = [...(petToProcess.documents || []), ...uploadedDocumentUrls];
            }

            delete petToProcess.pictureFile;
            delete petToProcess.newDocuments;
            
            let updatedPetsArray;
            if (editingPet) {
                updatedPetsArray = pets.map(p =>
                    p.id === editingPet.id ? { ...petToProcess, id: editingPet.id } : p
                );
            } else {
                petToProcess.id = Date.now();
                updatedPetsArray = [...pets, petToProcess];
            }

            await updateUserProfile({ pets: updatedPetsArray });
            
            setPets(updatedPetsArray);
            setMessage(editingPet ? 'Pet updated successfully!' : 'Pet added successfully!');
            setMessageType('info');
            setIsAddPetModalOpen(false);
            setEditingPet(null);
        } catch (error) {
            console.error("Error saving pet:", error);
            setMessage(`Error saving pet: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAddPetModal = () => {
        setIsAddPetModalOpen(false);
        setEditingPet(null);
        setMessage('');
    };

    const handleRemovePet = async (petIdToRemove) => {
        setMessage('');
        setLoading(true);
        try {
            const updatedPets = pets.filter(pet => pet.id !== petIdToRemove);
            await updateUserProfile({ pets: updatedPets });
            setPets(updatedPets);
            setMessage('Pet removed successfully!');
            setMessageType('info');
        } catch (error) {
            console.error("Error removing pet:", error);
            setMessage(`Error removing pet: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    // eslint-disable-next-line no-unused-vars
    const handleDocumentRemove = (petId, docUrlToRemove) => {
        setDocToRemove({ petId, docUrlToRemove });
    };

    const handleConfirmDocumentRemove = async () => {
        setMessage('');
        try {
            const petToUpdate = pets.find(p => p.id === docToRemove.petId);
            if (!petToUpdate) return;
            
            const updatedDocuments = petToUpdate.documents.filter(doc => doc.url !== docToRemove.docUrlToRemove);
            const updatedPets = pets.map(p => p.id === docToRemove.petId ? { ...p, documents: updatedDocuments } : p);
            
            await updateUserProfile({ pets: updatedPets });
            setPets(updatedPets);
            setMessage('Document removed successfully!');
            setMessageType('info');
        } catch (error) {
            console.error("Error removing document:", error);
            setMessage(`Error removing document: ${error.message}`);
            setMessageType('error');
        } finally {
            setDocToRemove(null);
        }
    };

    const handleCancelDocumentRemove = () => {
        setDocToRemove(null);
    };

    const handleDeRegisterClick = () => {
        setConfirmDeactivate(true);
    };

    const handleConfirmDeactivate = async () => {
        setConfirmDeactivate(false);
        setMessage('');
        setLoading(true);
        try {
            await updateUserProfile({ 
                isActive: false, 
                deactivatedAt: new Date(),
                availableSpots: 0
            });
            
            setMessage('Account successfully deactivated. You will be logged out now.');
            setMessageType('info');
            
            setTimeout(logout, 1500); 

        } catch (error) {
            console.error("Error deactivating account:", error);
            setMessage(`Error deactivating account: ${error.message}.`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };


    if (!userDetails) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)] bg-gray-50">
                <div className="text-xl font-semibold text-gray-700">Loading profile...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            {docToRemove && (
                <MessageBox
                    message="Are you sure you want to permanently remove this document?"
                    onClose={handleCancelDocumentRemove}
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button
                            onClick={handleConfirmDocumentRemove}
                            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            Confirm Delete
                        </button>
                        <button
                            onClick={handleCancelDocumentRemove}
                            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </MessageBox>
            )}
            
            {confirmDeactivate && (
                <MessageBox
                    message="WARNING: Are you sure you want to deactivate your account? Your data will be retained for administrative purposes (soft delete) but your account will be marked as inactive and you will be logged out. This action is irreversible by you and requires an administrator to re-activate."
                    onClose={() => setConfirmDeactivate(false)}
                    type="error"
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button
                            onClick={handleConfirmDeactivate}
                            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            Confirm Deactivation
                        </button>
                        <button
                            onClick={() => setConfirmDeactivate(false)}
                            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </MessageBox>
            )}

            <h2 className="font-display text-5xl text-gray-800 mb-6 text-center tracking-wider">
                Your Profile
            </h2>
            
            <div className="flex justify-end mb-4">
                 <button 
                    onClick={handleDeRegisterClick}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                 >
                    Deactivate Account
                 </button>
            </div>

            <form onSubmit={handleProfileUpdate} className="space-y-6 mb-10 p-6 border border-gray-200 rounded-lg shadow-sm">
                <h3 className="text-2xl font-semibold text-gray-700 mb-4">User Details</h3>
                <div className="flex flex-col items-center space-y-4">
                    {userDetails.userProfilePictureUrl ? (
                        <img src={userDetails.userProfilePictureUrl} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-blue-400 shadow-md" />
                    ) : (
                        <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm border-4 border-gray-300">No Photo</div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProfilePicFile(e.target.files[0])}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                </div>
                
                <div>
                    <label htmlFor="preferredDisplayName-profile" className="block text-sm font-medium text-gray-700 mb-1">Display Name (Used for Bookings)</label>
                    <input
                        type="text"
                        id="preferredDisplayName-profile"
                        value={displayNameState}
                        onChange={(e) => setDisplayNameState(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your preferred name (e.g., Dog Lover 77)"
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Current Display Name: <span className="font-bold text-blue-600">{userDetails.displayName}</span>
                    </p>
                </div>

                <div>
                    <label htmlFor="firstName-profile" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                        type="text"
                        id="firstName-profile"
                        value={firstNameState}
                        onChange={(e) => setFirstNameState(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your First Name"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="lastName-profile" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                        type="text"
                        id="lastName-profile"
                        value={lastNameState}
                        onChange={(e) => setLastNameState(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Last Name"
                        required
                    />
                </div>
                
                <div>
                    <label htmlFor="color-profile" className="block text-sm font-medium text-gray-700 mb-1">Your Booking Color</label>
                    <input
                        type="color"
                        id="color-profile"
                        value={newColor}
                        onChange={(e) => setNewColor(e.target.value)}
                        className="w-full h-10 rounded-lg border border-gray-300 cursor-pointer"
                    />
                </div>
                <div>
                    <label htmlFor="homeAddress" className="block text-sm font-medium text-gray-700 mb-1">Home Address</label>
                    <input
                        type="text"
                        id="homeAddress"
                        value={homeAddress}
                        onChange={(e) => setHomeAddress(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="123 Dog Park Lane"
                    />
                </div>
                <div>
                    <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name</label>
                    <input
                        type="text"
                        id="emergencyContactName"
                        value={emergencyContactName}
                        onChange={(e) => setEmergencyContactName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="John Doe"
                    />
                </div>
                <div>
                    <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
                    <input
                        type="tel"
                        id="emergencyContactPhone"
                        value={emergencyContactPhone}
                        onChange={(e) => setEmergencyContactPhone(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-border-blue-500"
                        placeholder="081-123-4567"
                    />
                </div>
                <div>
                    <label htmlFor="cellPhoneNumber" className="block text-sm font-medium text-gray-700 mb-1">Cell Phone Number</label>
                    <input
                        type="tel"
                        id="cellPhoneNumber"
                        value={cellPhoneNumber}
                        onChange={(e) => setCellPhoneNumber(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="555-987-6543"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                >
                    {loading ? 'Updating...' : 'Update Profile'}
                </button>
            </form>
            
            <form onSubmit={handlePasswordChange} className="space-y-6 mb-10 p-6 border border-gray-200 rounded-lg shadow-sm">
                <h3 className="text-2xl font-semibold text-gray-700 mb-4">Change Password</h3>
                
                <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                        placeholder="Your current password"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="New password (min. 6 characters)"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Confirm new password"
                        required
                    />
                </div>
                    <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                >
                    {loading ? 'Changing...' : 'Change Password'}
                </button>
            </form>

            <div className="mb-10 p-6 border border-gray-200 rounded-lg shadow-sm">
                <h3 className="text-2xl font-semibold text-gray-700 mb-4">Your Pets</h3>
                {pets.length === 0 ? (
                    <p className="text-gray-600 text-center mb-4">No pets added yet.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        {pets.map((pet, index) => (
                            <div key={pet.id || index} className="bg-gray-50 p-4 rounded-lg shadow-md border border-gray-100 flex flex-col items-center text-center">
                                {pet.pictureUrl ? (
                                    <img src={pet.pictureUrl} alt="Pet Profile" className="w-24 h-24 rounded-full object-cover mb-3 border-2 border-green-400" />
                                ) : (
                                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs mb-3">No Pet Photo</div>
                                )}
                                <p className="font-bold text-lg text-gray-800">{pet.name}</p>
                                <p className="text-sm text-gray-600">Type: {pet.type}</p>
                                <p className="text-sm text-gray-600">Breed: {pet.breed}</p>
                                <p className="text-sm text-gray-600">Gender: {pet.gender}</p>
                                <p className="text-sm text-gray-600">Born: {pet.dob || 'N/A'} ({getCalculatedAge(pet.dob)})</p>
                                <p className="text-sm text-gray-600">Color: {pet.color}</p>
                                <p className="text-sm text-gray-600">Microchip: {pet.microchipNumber} ({pet.microchipProvider})</p>
                                {pet.specialNeeds && (
                                    <p className="text-sm text-red-600 font-bold mt-2">Special Needs: {pet.specialNeeds}</p>
                                )}
                                {pet.documents && pet.documents.length > 0 && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        <p className="font-semibold">Documents:</p>
                                        <ul className="list-disc list-inside text-left">
                                            {pet.documents.map((doc, docIndex) => (
                                                <li key={docIndex} className="flex items-center justify-between truncate">
                                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                                        {doc.name}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="flex space-x-2 mt-3">
                                    <button
                                        onClick={() => handleEditPetClick(pet)}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 transition duration-200"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleRemovePet(pet.id || index)}
                                        className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 transition duration-200"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    onClick={handleOpenAddPetModal}
                    className="w-full py-3 px-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50"
                >
                    Add New Pet
                </button>
            </div>

            {isAddPetModalOpen && (
                <AddPetModal
                    isOpen={isAddPetModalOpen}
                    onClose={handleCloseAddPetModal}
                    onSave={handleSavePet}
                    initialPetData={editingPet}
                    loading={loading}
                />
            )}
        </div>
    );
}

export default ProfilePage;